import { FC, useEffect, useRef } from 'react';
import { translate } from '../../i18n';
import { BlockedCardIcon } from '../CardStatusModal/icons';
import {
  type ConfirmDeleteCardModalLayout,
  getConfirmDeleteCardModalStyles,
} from './styles';

export interface ConfirmDeleteCardModalProps {
  /** Últimos 4 dígitos de la tarjeta a eliminar. */
  last4: string;
  /** `sheet` en app; `dialog` centrado en web desktop. */
  layout?: ConfirmDeleteCardModalLayout;
  /** `true` mientras se elimina: bloquea los botones para evitar un segundo pedido. */
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  testID?: string;
}

/**
 * ConfirmDeleteCardModal — confirmación antes de eliminar una tarjeta. En mobile usa el mismo
 * bottom sheet que el modal de estado de tarjeta; en web desktop, un diálogo centrado con acciones
 * en fila y jerarquía visual de acción destructiva.
 */
export const ConfirmDeleteCardModal: FC<ConfirmDeleteCardModalProps> = ({
  last4,
  layout = 'sheet',
  loading = false,
  onConfirm,
  onCancel,
  testID = 'confirm-delete-card-modal',
}) => {
  const styles = getConfirmDeleteCardModalStyles(layout);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const descriptionId = `${testID}-description`;

  useEffect(() => {
    cancelRef.current?.focus();
  }, [layout, last4]);

  const isDialog = layout === 'dialog';

  return (
    <div style={styles.overlay} data-testid={testID} data-layout={layout}>
      <div
        style={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testID}-title`}
        aria-describedby={isDialog ? descriptionId : undefined}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          data-testid={`${testID}-close`}
          aria-label={translate('paymentsUI.confirmDeleteCardModal.closeAriaLabel')}
          style={styles.closeButton}
        >
          <img src={`${import.meta.env.BASE_URL}assets/card-status/close-button.svg`} alt="" width="40" height="40" />
        </button>

        <div style={styles.iconWrapper}>
          <BlockedCardIcon />
        </div>

        <h2 id={`${testID}-title`} style={styles.title}>
          {isDialog
            ? translate('paymentsUI.confirmDeleteCardModal.titleWeb')
            : translate('paymentsUI.confirmDeleteCardModal.title', { last4 })}
        </h2>

        {isDialog ? (
          <p id={descriptionId} style={styles.description}>
            {translate('paymentsUI.confirmDeleteCardModal.descriptionWebBefore')}
            <span style={styles.last4}>{last4}</span>
            {translate('paymentsUI.confirmDeleteCardModal.descriptionWebAfter')}
          </p>
        ) : null}

        <div style={styles.actions}>
          {isDialog ? (
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={loading}
              data-testid={`${testID}-cancel`}
              style={{ ...styles.cancelButton, ...(loading ? styles.disabled : {}) }}
            >
              {translate('paymentsUI.confirmDeleteCardModal.cancelButton')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            data-testid={`${testID}-confirm`}
            style={{ ...styles.doneButton, ...(loading ? styles.disabled : {}) }}
          >
            {translate('paymentsUI.confirmDeleteCardModal.confirmButton')}
          </button>
          {!isDialog ? (
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={loading}
              data-testid={`${testID}-cancel`}
              style={{ ...styles.cancelButton, ...(loading ? styles.disabled : {}) }}
            >
              {translate('paymentsUI.confirmDeleteCardModal.cancelButton')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
